import classNames from 'classnames';
import React, { Suspense, lazy } from 'react';

import { pascalCase } from '../../lib/utils';

import { QuizProvider } from '../../contexts';

import type { Block } from '../../types/blocks';
import ErrorBoundary from '../partials/ErrorBoundary';
import TextBlock from './Text';

import styles from '../../../styles/blocks/blocks.module.css';

type BlockComponent = React.ComponentType<any>;

// Lazy load block components, except Text which is common above the fold and would reflow the page
const lazyBlockComponents: {
    [key: string]: BlockComponent | React.LazyExoticComponent<BlockComponent>;
} = {
    Text: TextBlock,
    Quote: lazy(() => import('./Quote')),
    Link: lazy(() => import('./Link')),
    Podcast: lazy(() => import('./Podcast')),
    Embed: lazy(() => import('./Embed')),
    Image: lazy(() => import('./Image')),
    Stats: lazy(() => import('./Stats')),
    Logos: lazy(() => import('./Logos')),
    QuizResults: lazy(() => import('./QuizResults')),
    EventGroupsCarousel: lazy(() => import('./EventGroupsCarousel')),
    EventsCarousel: lazy(() => import('./EventsCarousel')),
    FeaturedEventsCarousel: lazy(() => import('./FeaturedEventsCarousel')),
    MagazinesCarousel: lazy(() => import('./SuggestedMagazines')),
    SuggestedMagazines: lazy(() => import('./SuggestedMagazines')),
    SuggestedContacts: lazy(() => import('./SuggestedContacts')),
    SuggestedEvents: lazy(() => import('./SuggestedEvents')),
    PhotosTimeline: lazy(() => import('./PhotosTimeline')),
    Quiz: lazy(() => import('./Quiz')),
    Blocks: lazy(() => import('./Blocks')),
    Interactive: lazy(() => import('./Interactive')),
    PhotosGrid: lazy(() => import('./PhotosGrid')),
    PhotosCarousel: lazy(() => import('./PhotosCarousel')),
    Title: lazy(() => import('./Title')),
};

// Loading fallback component
function BlockLoader() {
    return <div className={styles.blockLoader} aria-live="polite" aria-busy="true" />;
}

interface BlocksProps {
    page?: Page | null;
    blocks?: Block[];
    blocksAreSections?: boolean;
    className?: string | null;
    blockClassName?: string | null;
    withoutPadding?: boolean;
    isHomePage?: boolean;
    isPreview?: boolean;
    purplePage?: boolean;
}

function Blocks({
    blocks = [],
    blocksAreSections = false,
    page = null,
    className = null,
    blockClassName = null,
    withoutPadding = false,
    isHomePage = false,
    isPreview = false,
    purplePage = true,
}: BlocksProps) {
    const { type: pageType = null } = page || {};

    const isMagazine =
        pageType === 'magazine' ||
        pageType === 'magazine_video' ||
        pageType === 'magazine_podcast' ||
        pageType === 'magazine_photo';

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.withoutPadding]: withoutPadding,
                    [className]: className !== null,
                },
            ])}
        >
            <QuizProvider>
                {(blocks || []).map((block: Block, index = 0) => {
                    const { id = null, type = null, className: blockClassNameProp = null } = block;
                    // if (type === null) {
                    //     console.log('Block type is missing for block', block);
                    //     return null;
                    // }

                    const BlockType = pascalCase(type) || null;

                    const LazyBlockComponent = BlockType ? lazyBlockComponents[BlockType] : null;
                    const currentClassName = blockClassNameProp || null;

                    return LazyBlockComponent !== null && LazyBlockComponent !== undefined ? (
                        <div
                            // Unsaved blocks (Panneau preview) have no id: keys must stay unique
                            key={id !== null ? `block-${id}` : `block-new-${index}`}
                            className={classNames({
                                [styles.block]: true,
                                [blockClassName]: blockClassName !== null,
                                [currentClassName]: currentClassName !== null,
                                [styles[`type${BlockType}`]]: BlockType !== null,
                            })}
                        >
                            <ErrorBoundary>
                                <Suspense fallback={<BlockLoader />}>
                                    <LazyBlockComponent
                                        id={id}
                                        page={page}
                                        isSection={blocksAreSections}
                                        isHomePage={isHomePage}
                                        isPreview={isPreview}
                                        hasLettrine={index === 0 && isMagazine}
                                        purplePage={purplePage}
                                        {...block}
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        </div>
                    ) : null;
                })}
            </QuizProvider>
        </div>
    );
}

export default Blocks;
